import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { validate } from 'email-validator';
import ReCAPTCHA from 'react-google-recaptcha';
import { queryOrderByReferenceInternal } from '@adapters/bigcommerce/operations/orders/queryOrderByReferenceInternal';

interface UseReturnsFormOptions {
  onSuccess?: (orderId: string) => void;
  onError?: (message: string) => void;
}

const useReturnsForm = (options?: UseReturnsFormOptions) => {
  const { t } = useTranslation('returns');

  const [email, setEmail] = useState('');
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [postalCodeIsValid, setPostalCodeIsValid] = useState(false);
  const [orderNumber, setOrderNumber] = useState('SO-');
  const [orderNumberIsValid, setOrderNumberIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    setEmailIsValid(validate(email));
  }, [email]);

  useEffect(() => {
    setPostalCodeIsValid(!!postalCode.length);
  }, [postalCode]);

  useEffect(() => {
    setOrderNumberIsValid(orderNumber.length > 3);
  }, [orderNumber]);

  const handleValidation = useCallback(() => {
    const isValid = emailIsValid && orderNumberIsValid && postalCodeIsValid;
    if (dirty) {
      setDisabled(!isValid);
    }
    return isValid;
  }, [emailIsValid, orderNumberIsValid, postalCodeIsValid, dirty]);

  useEffect(() => {
    handleValidation();
  }, [handleValidation]);

  const handleSubmit = async (
    recaptchaRef: ReCAPTCHA | null,
    e: React.SyntheticEvent<EventTarget>
  ) => {
    e.preventDefault();
    if (!dirty) setDirty(true);
    const isValid = handleValidation();
    if (!isValid) return;

    setLoading(true);
    setErrorMessage('');
    try {
      const recaptchaToken = await recaptchaRef?.executeAsync();
      recaptchaRef?.reset();

      const orderResponse = await queryOrderByReferenceInternal({
        email,
        postalCode,
        orderNumber,
        recaptchaToken,
      });
      if (orderResponse && orderResponse.data) {
        const order = orderResponse.data;
        if (options?.onSuccess) {
          options.onSuccess(String(order.id));
        }
        setLoading(false);
        return;
      }

      const failMsg = t('returns.lookup.notFound');
      const helpMsg = t('returns.lookup.notFound.help');
      setErrorMessage(`${failMsg} ${helpMsg}`);
      if (options?.onError) options.onError(`${failMsg} ${helpMsg}`);
    } catch (error: unknown) {
      const message = t('returns.lookup.failed');
      setErrorMessage(message);
      if (options?.onError) options.onError(message);
    } finally {
      setLoading(false);
    }
  };

  return {
    email: {
      value: email,
      set: setEmail,
      isValid: emailIsValid,
    },
    orderNumber: {
      value: orderNumber,
      set: setOrderNumber,
      isValid: orderNumberIsValid,
    },
    postalCode: {
      value: postalCode,
      set: setPostalCode,
      isValid: postalCodeIsValid,
    },
    submitDisabled: disabled,
    isLoading: loading,
    isDisabled: disabled,
    isDirty: dirty,
    errorMessage,
    handleSubmit,
  };
};

export default useReturnsForm;
